import React, { useEffect, useMemo, useState } from "react";
import createDOMPurify from "dompurify";
import { getSettings } from "../../services/settingsServices";
import SectionTitle from "../../components/ui/SectionTitle";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

/* ---------------------------
   DOMPurify instance + styles whitelist
---------------------------- */
const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : createDOMPurify;

const isColor = (v) => {
  const s = String(v || "").trim().toLowerCase();
  // formats courants + quelques mots clés
  return (
    s === "transparent" ||
    s === "currentcolor" ||
    s === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(s) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(s) ||
    /^[a-z]+$/i.test(s) // "red", "white", etc.
  );
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED = {
    color: (v) => isColor(v),
    "background-color": (v) => isColor(v),
    "text-align": (v) => ["left", "right", "center", "justify"].includes(String(v).trim().toLowerCase()),
    "font-weight": (v) => /^(normal|bold|bolder|lighter|[1-9]00)$/.test(String(v).trim().toLowerCase()),
    "font-style": (v) => /^(normal|italic|oblique)$/.test(String(v).trim().toLowerCase()),
    "text-decoration": (v) =>
      /^(none|underline|line-through|overline)$/.test(String(v).trim().toLowerCase()),
  };

  const cleaned = String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const idx = rule.indexOf(":");
      if (idx === -1) return null;
      const prop = rule.slice(0, idx).trim().toLowerCase();
      const val = rule.slice(idx + 1).trim();
      const ok = ALLOWED[prop]?.(val);
      return ok ? `${prop}: ${val}` : null;
    })
    .filter(Boolean);

  return cleaned.join("; ");
};

// Hook local (sur l’instance purify)
purify.removeAllHooks();
purify.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName === "style") {
    const clean = sanitizeInlineStyle(data.attrValue);
    if (clean) data.attrValue = clean;
    else data.keepAttr = false;
  }
});

purify.addHook("afterSanitizeAttributes", (node) => {
  // sécurise les liens ouvrant un nouvel onglet
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const InfoClubSkeleton = () => (
  <section className="relative py-12 sm:py-16 bg-slate-50 dark:bg-slate-950">
    <div className="mx-auto max-w-7xl px-4">
      <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 overflow-hidden">
        <div className="grid lg:grid-cols-12 gap-0">
          <div className="lg:col-span-8 p-6 sm:p-10 space-y-4">
            <div className="h-7 w-2/3 bg-slate-200/70 dark:bg-slate-800 rounded-xl animate-pulse" />
            <div className="h-4 w-1/2 bg-slate-200/70 dark:bg-slate-800 rounded animate-pulse" />
            <div className="mt-6 space-y-3">
              <div className="h-4 w-full bg-slate-200/70 dark:bg-slate-800 rounded animate-pulse" />
              <div className="h-4 w-[92%] bg-slate-200/70 dark:bg-slate-800 rounded animate-pulse" />
              <div className="h-4 w-[86%] bg-slate-200/70 dark:bg-slate-800 rounded animate-pulse" />
            </div>
          </div>
          <div className="lg:col-span-4 h-56 lg:h-auto bg-slate-200/70 dark:bg-slate-800 animate-pulse" />
        </div>
      </div>
    </div>
  </section>
);

const InfoClub = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        if (Array.isArray(data) && data.length > 0) setSettings(data[0]);
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const safeHtml = useMemo(() => {
    const raw = settings?.infoclub_content || "";
    return purify.sanitize(raw, {
      USE_PROFILES: { html: true },
      // ✅ on autorise style mais filtré par notre hook
      ADD_ATTR: ["style", "target", "rel"],
      // ✅ on bloque les balises <style> (pas d’injection CSS globale)
      FORBID_TAGS: ["style"],
    });
  }, [settings?.infoclub_content]);

  if (loading) return <InfoClubSkeleton />;
  if (!settings?.infoclub_isactive) return null;

  const title = settings?.infoclub_title || "Info Club";

  return (
    <section
      className={cn(
        "relative py-12 sm:py-16",
        "bg-lightBackground dark:bg-darkBackground",
        "text-slate-900 dark:text-slate-50"
      )}
    >
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0",
          "bg-[radial-gradient(900px_circle_at_15%_10%,rgba(99,102,241,0.10),transparent_55%),radial-gradient(700px_circle_at_85%_35%,rgba(236,72,153,0.06),transparent_60%)]"
        )}
      />

      <style>{`
        @keyframes ic-float { 0%,100%{ transform: translateY(0)} 50%{ transform: translateY(-6px)} }
        @media (prefers-reduced-motion: reduce) {
          .ic-anim { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="relative mx-auto max-w-7xl px-4">
        <div
          className={cn(
            "relative overflow-hidden rounded-3xl",
            "border border-slate-200/70 dark:border-slate-800",
            "bg-white/75 dark:bg-slate-900/60 backdrop-blur",
            "shadow-sm"
          )}
        >
          <div aria-hidden className="absolute inset-0 rounded-3xl ring-1 ring-primary/20" />

          <div
            aria-hidden
            className="ic-anim absolute -top-16 -left-16 h-52 w-52 rounded-full bg-primary/16 blur-3xl"
            style={{ animation: "ic-float 3.4s ease-in-out infinite" }}
          />

          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-8 p-6 sm:p-10">
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                  <span className="inline-block h-2 w-2 rounded-full bg-primary ic-anim animate-pulse" />
                  Message important
                </span>
              </div>

              <SectionTitle
                align="left"
                title={title}
                subtitle={settings?.infoclub_subtitle || ""}
              />

              <div
                className={cn(
                  "mt-8",
                  "prose prose-slate dark:prose-invert max-w-none",
                  "prose-p:leading-relaxed prose-p:text-[15px] sm:prose-p:text-[16px]",
                  "prose-a:text-primary hover:prose-a:underline",
                  "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                  "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold"
                )}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </div>

            <div className="lg:col-span-4 relative">
              <div className="h-60 lg:h-full w-full">
                <img
                  src="/img/infoclub.png"
                  alt={title}
                  loading="lazy"
                  className={cn("h-full w-full object-cover", "transition duration-500")}
                />
              </div>

              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent dark:from-slate-950/55"
              />

              <div
                aria-hidden
                className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary/0 via-primary/60 to-primary/0"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InfoClub;
